import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import capitalize from 'lodash/capitalize'
import React, { useCallback } from 'react'
import { useFormContext } from 'react-hook-form'

import { DEFAULT_STATE } from 'components/common/address/model'
import BillingProfileDropdown from 'components/customer-details/content/common/billing-dropdown'
import BusinessUnitDropdown from 'components/customer-details/content/common/business-unit-dropdown/BusinessUnitDropdown'
import { HHFormTextField } from 'components/form-fields/v5'
import HHFormPhoneFieldUntypedImport from 'components/form-fields/v5/HHFormPhoneField'
import { DS_OVERLINE_SX } from 'components/taxes/common/dsTypography'
import { DRAWER_POPPER_SX } from 'components/taxes/tax-drawer/taxDrawerForm'
import T from 'T'
import { REGEX } from 'utils/validations'

import FillName from './FillName'
import NewAccountSourceFields from './NewAccountSourceFields'

import type { NewAccountFormValues } from './model'
import type { NewAccountLifecycle } from './useNewAccountLifecycle'

const { accountNamePlaceholder } = DEFAULT_STATE

// HHFormPhoneField is untyped .jsx: its defaults infer half its props as `undefined`.
const HHFormPhoneField = HHFormPhoneFieldUntypedImport as React.ElementType

type NewAccountDetailsFieldsProps = {
  lifecycle: NewAccountLifecycle
  onKeyDown: (event: React.KeyboardEvent) => void
}

const NewAccountDetailsFields = ({ lifecycle, onKeyDown }: NewAccountDetailsFieldsProps) => {
  const {
    control,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<NewAccountFormValues>()
  const { accountName: accountNameError, contactPhone: contactPhoneError, contactEmail: contactEmailError } = errors
  const { businessUnitId: businessUnitError, billingProfileId: billingProfileError } = errors
  const accountNameHelperText = accountNameError?.message
  const contactPhoneHelperText = contactPhoneError?.type === 'required' ? T.CANNOT_BE_EMPTY : contactPhoneError?.message
  const contactEmailHelperText = contactEmailError?.message
  const watchBillingProfileId = watch('billingProfileId')
  const watchBusinessUnitId = watch('businessUnitId')
  const watchAccountName = watch('accountName')

  // Neither dropdown registers, so react-hook-form never revalidates it: the mark stays until cleared.
  const handleBusinessUnitChange = useCallback(
    (businessUnitId: string) => {
      clearErrors('businessUnitId')
      setValue('businessUnitId', businessUnitId, { shouldDirty: true })
    },
    [clearErrors, setValue]
  )

  const handleFillName = useCallback(
    (firstName: string, lastName: string) => {
      setValue('contact.firstName', firstName, { shouldDirty: true })
      setValue('contact.lastName', lastName, { shouldDirty: true })
    },
    [setValue]
  )

  const handleBillingProfileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      clearErrors('billingProfileId')
      setValue('billingProfileId', event.target.value, { shouldDirty: true })
    },
    [clearErrors, setValue]
  )

  return (
    <>
      <Grid size={12}>
        <Typography color="text.secondary" sx={DS_OVERLINE_SX} variant="subtitle2">
          {T.ACCOUNT}
        </Typography>
      </Grid>
      <Grid size={12}>
        <HHFormTextField
          fullWidth
          required
          control={control}
          deprecatedLabel={false}
          error={accountNameHelperText}
          helperText={accountNameHelperText}
          label={capitalize(T.CUSTOMER_NAME)}
          name="accountName"
          placeholder={accountNamePlaceholder}
          rules={{ required: T.CANNOT_BE_EMPTY }}
          onKeyDown={onKeyDown}
        />
      </Grid>
      <Grid container alignItems="center" spacing={1.5} width="100%">
        <Grid size={{ xs: 12, sm: 6 }}>
          <HHFormTextField
            fullWidth
            control={control}
            deprecatedLabel={false}
            label={T.CONTACT_FIRST_NAME}
            name="contact.firstName"
            placeholder={T.CONTACT_FIRST_NAME}
            onKeyDown={onKeyDown}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 'grow' }}>
          <HHFormTextField
            fullWidth
            control={control}
            deprecatedLabel={false}
            label={T.CONTACT_LAST_NAME}
            name="contact.lastName"
            placeholder={T.CONTACT_LAST_NAME}
            onKeyDown={onKeyDown}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 'auto' }}>
          <FillName customerName={watchAccountName} onClick={handleFillName} />
        </Grid>
      </Grid>
      <Grid container spacing={1.5} width="100%">
        <Grid size={{ xs: 12, sm: 6 }}>
          <HHFormPhoneField
            fullWidth
            control={control}
            deprecatedLabel={false}
            error={contactPhoneHelperText}
            helperText={contactPhoneHelperText}
            label={T.CONTACT_PHONE}
            name="contactPhone"
            placeholder={T.CONTACT_PHONE_NUMBER}
            required={lifecycle.requirePhone}
            rules={{ pattern: { value: REGEX.IS_VALID_MOBILE, message: T.MUST_BE_A_VALID_PHONE_NUMBER } }}
            onKeyDown={onKeyDown}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {/* Required here means the asterisk only: a blank email is answered by the print-only prompt, not by a form error. */}
          <HHFormTextField
            fullWidth
            control={control}
            deprecatedLabel={false}
            error={contactEmailHelperText}
            helperText={contactEmailHelperText}
            label={T.BILLING_EMAIL}
            name="contactEmail"
            placeholder={T.BILLING_EMAIL}
            required={lifecycle.requireBillingEmail}
            rules={{ pattern: { value: REGEX.IS_EMAIL, message: T.MUST_BE_A_VALID_EMAIL_ADDRESS } }}
            onKeyDown={onKeyDown}
          />
          {lifecycle.requireBillingEmail && (
            <Typography color="text.secondary" fontWeight={400} ml={1.75} mt={0.5} variant="subtitle2">
              {T.BLANK_EMAIL_MEANS_PRINTED_COPY}
            </Typography>
          )}
        </Grid>
      </Grid>
      <Grid size={12}>
        <BillingProfileDropdown
          required
          billingProfileId={watchBillingProfileId}
          errorText={billingProfileError?.message}
          menuSx={DRAWER_POPPER_SX}
          onChange={handleBillingProfileChange}
        />
      </Grid>
      <BusinessUnitDropdown
        allowNone
        errorText={businessUnitError?.message}
        menuSx={DRAWER_POPPER_SX}
        required={lifecycle.requireBusinessUnit}
        value={watchBusinessUnitId}
        onChange={handleBusinessUnitChange}
      />
      <NewAccountSourceFields lifecycle={lifecycle} />
    </>
  )
}

export default NewAccountDetailsFields
